import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { Card, ErrorState, LoadingBlock, StatusDot } from '@/components/ui';
import {
  canonicalCatalogName,
  catalogNameLanguage,
  compareCatalogDisplayOrder,
  formatDateTime,
  formatDuration,
  sessionTone,
  SESSION_STATUS_LABEL,
} from '@/lib/format';
import { catalogBanner, catalogCover, catalogThumb } from '@/lib/gameCatalog';
import { copy, localizeCatalogName, useI18n } from '@/lib/i18n';
import type { BannerSlide, CatalogItem, Endpoint, SessionItem } from '@shared/index';
import './Home.css';

/** Müşteri ana sayfası: katalog vitrini değil, günlük operasyon özeti. */
export default function Home() {
  const me = useMe();
  const { language, locale } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const now = useMemo(() => new Date(), []);
  const weekEnd = useMemo(() => {
    const value = new Date(now);
    value.setDate(value.getDate() + 7);
    return value;
  }, [now]);

  const owned = useQuery({
    queryKey: ['catalog', { ownedOnly: true }],
    queryFn: () => api.get<{ items: CatalogItem[] }>('/catalog', { ownedOnly: true }),
  });

  const endpoints = useQuery({
    queryKey: ['endpoints'],
    queryFn: () => api.get<{ items: Endpoint[] }>('/endpoints'),
    enabled: me.nav.includes('operations') && (owned.data?.items.length ?? 0) > 0,
  });

  const upcoming = useQuery({
    queryKey: ['sessions', 'home-week'],
    queryFn: () =>
      api.get<{ items: SessionItem[] }>('/sessions', {
        from: now.toISOString(),
        to: weekEnd.toISOString(),
        pageSize: 100,
      }),
    enabled: me.nav.includes('operations') && (owned.data?.items.length ?? 0) > 0,
  });

  const recent = useQuery({
    queryKey: ['sessions', 'home-recent'],
    queryFn: () => api.get<{ items: SessionItem[] }>('/sessions', { status: 'completed', sort: 'desc', pageSize: 6 }),
    enabled: me.nav.includes('operations') && (owned.data?.items.length ?? 0) > 0,
  });

  const discovery = useQuery({
    queryKey: ['catalog', 'home-discovery'],
    queryFn: () => api.get<{ items: CatalogItem[] }>('/catalog', { ownedOnly: false }),
    enabled: owned.isSuccess && (owned.data?.items.length ?? 0) === 0,
  });

  const endpointItems = endpoints.data?.items ?? [];
  const sessionItems = upcoming.data?.items ?? [];
  const ownedItems = [...(owned.data?.items ?? [])].sort(
    (a, b) => compareCatalogDisplayOrder(a.category, b.category) || a.name.localeCompare(b.name, 'tr-TR'),
  );
  const nextSession = sessionItems.find((item) => item.status === 'scheduled');
  const lastPlayed = recent.data?.items[0] ?? null;
  const newlyAdded = [...ownedItems].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)).slice(0, 3);
  const needsAttention = endpointItems.filter((item) => item.status === 'offline');
  const hasDesktopProduct = ownedItems.some((item) => item.delivery === 'desktop-install');

  if (
    owned.isLoading ||
    (ownedItems.length === 0 && discovery.isLoading) ||
    (ownedItems.length > 0 && me.nav.includes('operations') && (endpoints.isLoading || upcoming.isLoading || recent.isLoading))
  ) {
    return <LoadingBlock rows={6} label={c('Operasyon özeti yükleniyor', 'Loading operation overview')} />;
  }

  if (owned.isError || discovery.isError || endpoints.isError || upcoming.isError || recent.isError) {
    return (
      <ErrorState
        message={c('Bugünün operasyon özeti alınamadı.', "Today's operation overview could not be loaded.")}
        onRetry={() => {
          void endpoints.refetch();
          void upcoming.refetch();
          void recent.refetch();
          void owned.refetch();
          void discovery.refetch();
        }}
      />
    );
  }

  if (me.activation && me.activation.status !== 'approved' && ownedItems.length === 0) {
    return (
      <ActivationHome
        accountName={me.account.name}
        activation={me.activation}
        items={discovery.data?.items ?? []}
      />
    );
  }

  if (ownedItems.length === 0) {
    return <DiscoveryHome accountName={me.account.name} items={discovery.data?.items ?? []} />;
  }

  return (
    <div className="home-dashboard stack stack--lg">
      <header className="home-dashboard__head">
        <div>
          <p className="home-dashboard__date">
            {now.toLocaleDateString(locale, {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
              weekday: 'long',
            })}
          </p>
          <h1>{c('Bugün', 'Today')}</h1>
          <p className="muted">{c('İçeriklerinizi, cihazlarınızı ve planlanmış oturumlarınızı yönetin.', 'Manage your content, devices and scheduled sessions.')}</p>
        </div>
        {me.nav.includes('operations') && (
          <Link className="btn btn--primary btn--md" to="/operasyon/takvim?planla=1">
            {c('Oturum planla', 'Schedule session')}
          </Link>
        )}
      </header>

      {me.nav.includes('operations') ? (
        <div className="home-dashboard__leadgrid">
          <section className="home-priority" aria-labelledby="priority-title">
            <div className="home-priority__copy">
              <h2 id="priority-title">
                {ownedItems.length === 0
                  ? c('Kataloğunuzu oluşturmaya başlayın', 'Start building your catalog')
                  : endpointItems.length === 0
                    ? hasDesktopProduct ? c('CORTEX cihazınızı bağlayın', 'Connect your CORTEX device') : c('İlk deneyim noktanızı ekleyin', 'Add your first experience point')
                  : needsAttention.length > 0
                    ? c('Cihaz bağlantılarını kontrol edin', 'Check device connections')
                    : c('Deneyimleriniz operasyona hazır', 'Your experiences are ready to operate')}
              </h2>
              <p>
                {ownedItems.length === 0
                  ? c('VR Deneyimleri, Etkileşimli Dijital Sanat, AR Deneyimleri, Leds Play ve yeni katalog ürünlerini keşfedin.', 'Explore VR Experiences, Immersive Digital Art, AR Experiences, Leds Play and future catalogs.')
                  : endpointItems.length === 0
                    ? hasDesktopProduct
                      ? c('Satın aldığınız PC içeriğini çalıştırmak için CORTEX yüklü cihazınızı tanımlayın.', 'Register a device with CORTEX installed to run your purchased PC content.')
                      : c('Hizmetinizin çalışacağı ekranı, istasyonu veya kurulum noktasını tanımlayın.', 'Define the screen, station or installation point where your service will run.')
                  : needsAttention.length > 0
                    ? `${needsAttention.length} ${c('cihaz veya deneyim noktası şu anda çevrimdışı görünüyor.', 'devices or experience points currently appear offline.')}`
                    : c('Bağlantılar çevrimiçi. İçeriğinizi açabilir veya sıradaki oturumu planlayabilirsiniz.', 'Connections are online. You can launch content or schedule the next session.')}
              </p>
              <Link className="btn btn--secondary btn--md" to={ownedItems.length === 0 ? '/kesfet' : '/operasyon/ekranlar'}>
                {ownedItems.length === 0 ? c('Keşfet', 'Discover') : endpointItems.length === 0 ? c('Cihaz veya nokta ekle', 'Add a device or point') : c('Bağlantıları kontrol et', 'Check connections')}
              </Link>
            </div>
            <img
              className="home-priority__image"
              src={catalogBanner(ownedItems[0]!) ?? '/media/technology-v2/immersive.webp'}
              alt=""
            />
          </section>

          <Card title={c('Sıradaki oturum', 'Next session')} className="home-next">
            {nextSession ? (
              <div className="home-next__content">
                <StatusDot tone={sessionTone(nextSession.status) ?? 'neutral'} label={language === 'tr' ? SESSION_STATUS_LABEL[nextSession.status] ?? nextSession.status : sessionStatusEnglish(nextSession.status)} />
                <h2>{nextSession.productName}</h2>
                <p className="muted">{nextSession.endpointName}</p>
                <dl className="home-next__facts">
                  <div><dt>{c('Başlangıç', 'Start')}</dt><dd>{formatDateTime(nextSession.startsAt)}</dd></div>
                  <div><dt>{c('Süre', 'Duration')}</dt><dd>{language === 'tr' ? formatDuration(nextSession.durationMinutes) : `${nextSession.durationMinutes} min`}</dd></div>
                </dl>
                <Link className="btn btn--secondary btn--md" to="/operasyon/oturumlar">{c('Oturumları aç', 'Open sessions')}</Link>
              </div>
            ) : (
              <div className="home-next__empty">
                <CalendarIcon />
                <h2>{c('Planlanmış oturum yok', 'No scheduled sessions')}</h2>
                <p className="muted">{c('Yeni bir oturum oluşturarak başlayın.', 'Start by creating a new session.')}</p>
                <Link className="btn btn--secondary btn--md" to="/operasyon/takvim?planla=1">{c('Oturum planla', 'Schedule session')}</Link>
              </div>
            )}
          </Card>
        </div>
      ) : (
        <Card className="home-dashboard__welcome">
          <h2>{c('İçeriklerinizi keşfedin', 'Discover your content')}</h2>
          <p className="muted">{c('Hesabınıza tanımlı deneyimlere ve katalogdaki yeni içeriklere ulaşın.', 'Access experiences assigned to your account and new catalog content.')}</p>
          <Link className="btn btn--primary btn--md" to="/kesfet">{c('Keşfet', 'Discover')}</Link>
        </Card>
      )}

      {me.nav.includes('operations') && (
        <Card title={c('Cihaz ve deneyim noktaları', 'Devices and experience points')} action={<Link className="home-dashboard__textlink" to="/operasyon/ekranlar">{c('Tümünü aç →', 'Open all →')}</Link>} padded={false}>
          {endpointItems.length === 0 ? (
            <div className="home-screens__empty">
              <p>{c('Henüz cihaz veya deneyim noktası tanımlanmadı.', 'No device or experience point has been defined yet.')}</p>
              <Link className="btn btn--secondary btn--sm" to="/operasyon/ekranlar">{c('Ekle', 'Add')}</Link>
            </div>
          ) : (
            <ul className="home-screens">
              {endpointItems.slice(0, 4).map((endpoint) => (
                <li key={endpoint.id} className="home-screens__row">
                  <ScreenIcon />
                  <div className="home-screens__name"><strong>{endpoint.name}</strong><span>{endpoint.location || c('Konum belirtilmedi', 'Location not specified')}</span></div>
                  <StatusDot tone={endpoint.status === 'offline' ? 'warning' : 'success'} label={endpoint.status === 'playing' ? c('Yayında', 'Playing') : endpoint.status === 'online' ? c('Çevrimiçi', 'Online') : c('Çevrimdışı', 'Offline')} />
                  <Link className="home-dashboard__textlink" to="/operasyon/ekranlar">{c('Kontrol et', 'Check')}</Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {(lastPlayed || newlyAdded.length > 0) && (
        <section className="home-content-layers" aria-labelledby="home-content-title">
          <div className="home-content-layers__head"><div><span>{c('Kütüphaneniz', 'Your library')}</span><h2 id="home-content-title">{c('Kaldığınız yerden devam edin.', 'Continue where you left off.')}</h2></div><Link className="home-dashboard__textlink" to="/kesfet">{c('Tümünü aç →', 'Open all →')}</Link></div>
          <div className="home-content-layers__grid">
            {lastPlayed && <article className="home-content-feature"><div>{c('Son oynatılan', 'Last played')}</div><h3>{lastPlayed.productName}</h3><p>{lastPlayed.endpointName} · {formatDateTime(lastPlayed.startsAt)}</p>{lastPlayed.productId && <Link className="btn btn--secondary btn--sm" to={`/kesfet/${ownedItems.find((item) => item.id === lastPlayed.productId)?.slug ?? ''}`}>{c('Ürünü aç', 'Open product')}</Link>}</article>}
            <div className="home-content-new"><span>{c('Yeni eklenenler', 'Newly added')}</span>{newlyAdded.map((item) => <Link key={item.id} to={`/kesfet/${item.slug}`}><img src={catalogThumb(item) ?? catalogCover(item) ?? ''} alt="" /><span><strong>{item.name}</strong><small>{localizeCatalogName(item.category, language)}</small></span><b aria-hidden="true">→</b></Link>)}</div>
          </div>
        </section>
      )}

      <div className="home-dashboard__lower">
        <WeekAgenda now={now} sessions={sessionItems} />
        <Card title={c('Kısayollar', 'Quick links')} padded={false}>
          <nav className="home-shortcuts" aria-label={c('Ana sayfa kısayolları', 'Home quick links')}>
            <Link to="/kesfet"><DocumentIcon />{c('İçerik seç', 'Choose content')}<span>→</span></Link>
            <Link to="/paket-bulucu"><CreditIcon />{c('Paket Bulucu', 'Package Finder')}<span>→</span></Link>
            {me.nav.includes('operations') && <Link to="/operasyon/ekranlar"><ScreenIcon />{c('Cihazları aç', 'Open devices')}<span>→</span></Link>}
            <Link to="/abonelikler"><CreditIcon />{c('Aboneliği incele', 'Review subscription')}<span>→</span></Link>
          </nav>
        </Card>
      </div>
    </div>
  );
}

function ActivationHome({
  accountName,
  activation,
  items,
}: {
  accountName: string;
  activation: NonNullable<ReturnType<typeof useMe>['activation']>;
  items: CatalogItem[];
}) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const [phone, setPhone] = useState('');
  const [contactSaved, setContactSaved] = useState(false);
  const savePhone = useMutation({
    mutationFn: () => api.patch('/account/onboarding/contact', { preference: 'phone', phone }),
    onSuccess: () => setContactSaved(true),
  });
  const catalog = activation.sourceCatalog ?? items[0]?.category ?? 'Wondeon deneyimleri';
  const localizedCatalog = localizeCatalogName(catalog, language);

  return (
    <div className="activation-home stack stack--lg">
      <section className="activation-home__hero">
        <div className="activation-home__copy">
          <span className="activation-home__eyebrow">{c('Kurum doğrulaması', 'Organization verification')}</span>
          <h1>{c(`${accountName} çalışma alanı hazırlanıyor.`, `The ${accountName} workspace is being prepared.`)}</h1>
          <p>
            {c('Başvurunuz', 'Your application for')} <strong>{localizedCatalog}</strong> {c('bağlamıyla alındı. Kurum bilgilerinizi incelerken size yalnızca ilgili seçkiyi gösteriyoruz.', 'has been received. While we review your organization details, we only show the relevant collection.')}
          </p>
          <div className="activation-home__status">
            <span aria-hidden="true" />
            {activation.status === 'rejected' ? c('Ek bilgi gerekiyor', 'Additional information required') : c('İnceleme sürüyor', 'Review in progress')}
          </div>
        </div>
        <img src={catalogBanner(items[0] ?? { category: catalog } as CatalogItem) ?? '/media/immersive-v2/hareketin-yankisi.webp'} alt="" />
      </section>

      <div className="activation-home__grid">
        <section className="activation-home__contact">
          <div>
            <span className="activation-home__step">{c('Sonraki adım', 'Next step')}</span>
            <h2>{c('Tanışma biçiminizi seçin', 'Choose how to meet us')}</h2>
            <p>{c('Kurumunuzu ve kullanım alanınızı doğrulamak için telefonla görüşebilir veya uygun bir Meet saati seçebilirsiniz.', 'To verify your organization and intended use, you can speak by phone or choose an available Meet time.')}</p>
          </div>
          <div className="activation-home__actions">
            <Link className="btn btn--primary btn--md" to="/takvim/randevu">{c('Meet saati seç', 'Choose a Meet time')}</Link>
            <div className="activation-home__phone">
              <label htmlFor="activation-phone">{c('Telefon numarası', 'Phone number')}</label>
              <div><input id="activation-phone" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="+90 5xx xxx xx xx" /><button className="btn btn--secondary btn--md" type="button" disabled={phone.trim().length < 7 || savePhone.isPending} onClick={() => savePhone.mutate()}>{contactSaved ? c('Kaydedildi', 'Saved') : c('Beni arayın', 'Call me')}</button></div>
            </div>
          </div>
        </section>

        <aside className="activation-home__privacy">
          <span aria-hidden="true">◌</span>
          <h2>{c('İçerik görünürlüğü kontrollü', 'Content visibility is controlled')}</h2>
          <p>{c('Aktivasyon tamamlanana kadar katalog içeriğinin yalnızca sınırlı önizlemesi açıktır. Satın alma sonrası cihaz, takvim ve rapor alanları otomatik açılır.', 'Only a limited catalog preview is available until activation is complete. Device, calendar and report areas open automatically after purchase.')}</p>
        </aside>
      </div>

      {items.length > 0 && (
        <section>
          <div className="home-discovery__sectionhead"><div><h2>{localizedCatalog} {c('seçkisi', 'collection')}</h2><p>{c('Başvurunuzla ilgili ilk ürün önizlemeleri; ayrıntılar aktivasyon sonrası açılır.', 'Initial product previews related to your application; details open after activation.')}</p></div></div>
          <div className="grid-covers">{items.slice(0, 4).map((item) => <ActivationProductPreview key={item.id} item={item} />)}</div>
        </section>
      )}
    </div>
  );
}

function ActivationProductPreview({ item }: { item: CatalogItem }) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const hue = categoryHue(item.category);
  const cover = catalogCover(item);
  const category = canonicalCatalogName(item.category);
  return (
    <article className="pcard" aria-label={`${item.name} ${c('ürün önizlemesi', 'product preview')}`}>
      <div className={`pcard__cover pcard__cover--${hue}`}>
        {cover ? <img src={cover} alt="" loading="lazy" /> : <span className="pcard__monogram" aria-hidden="true">{item.name.trim().charAt(0).toLocaleUpperCase('tr')}</span>}
        <span className="pcard__lock" title={c('Aktivasyon sonrası açılır', 'Opens after activation')}><LockIcon /></span>
      </div>
      <div className="pcard__meta">
        <span className="pcard__kicker" lang={catalogNameLanguage(category)}><span className={`catdot catdot--${hue}`} aria-hidden="true" />{localizeCatalogName(category, language)}</span>
        <h3 className="pcard__name">{item.name}</h3>
        <span className="small muted">{c('Aktivasyon bekleniyor', 'Awaiting activation')}</span>
      </div>
    </article>
  );
}

function DiscoveryHome({ accountName, items }: { accountName: string; items: CatalogItem[] }) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const orderedItems = [...items].sort(
    (a, b) => compareCatalogDisplayOrder(a.category, b.category) || a.name.localeCompare(b.name, 'tr-TR'),
  );
  const technologies = [
    {
      name: 'Leds Play',
      nameEn: 'Leds Play',
      language: undefined,
      text: 'Kamusal ekranları, ziyaretçilerin telefonlarıyla katıldığı ortak oyun alanlarına dönüştürün.',
      textEn: 'Turn public screens into shared play spaces that visitors join with their phones.',
      image: '/media/games-v2/carpisan-araba-banner.webp',
    },
    {
      name: 'Etkileşimli Dijital Sanat',
      nameEn: 'Immersive Digital Art',
      language: 'en',
      text: 'Motion Track ile sahne hareketine ve mekâna gerçek zamanlı tepki veren görsel dünyalar kurun.',
      textEn: 'Build visual worlds that respond to stage movement and the venue in real time with Motion Track.',
      image: '/media/immersive-v2/gelgit-odasi.webp',
    },
    {
      name: 'VR deneyimleri',
      nameEn: 'VR experiences',
      language: undefined,
      text: 'CORTEX üzerinden lisanslanan içerikleri cihazlarınıza ulaştırın ve tek noktadan yönetin.',
      textEn: 'Deliver licensed content to your devices through CORTEX and manage it from one place.',
      image: '/media/vr-v2/vr-arena.webp',
    },
    {
      name: 'AR Deneyimleri',
      nameEn: 'AR Experiences',
      language: undefined,
      text: 'Fiziksel mekânı, ziyaretçinin kendi cihazıyla keşfettiği katmanlı deneyimlere açın.',
      textEn: 'Transform a physical venue into layered experiences visitors explore with their own devices.',
      image: '/media/ar-v2/dinozor-parki-ar.webp',
    },
  ];

  return (
    <div className="home-discovery stack stack--lg">
      <header className="home-dashboard__head home-discovery__head">
        <div>
          <p className="home-dashboard__date">Wondeon Hub</p>
          <h1>{c(`${accountName} için yeni bir deneyim alanı kurun.`, `Build a new experience space for ${accountName}.`)}</h1>
          <p>{c('Teknolojiyi seçin; ürün, lisans, cihaz ve operasyon akışını aynı Hub üzerinden yönetin.', 'Choose the technology and manage products, licenses, devices and operations through the same Hub.')}</p>
        </div>
        <Link className="btn btn--primary btn--md" to="/kesfet">{c('Kataloğu keşfet', 'Explore the catalog')}</Link>
      </header>

      <section className="home-discovery__hero">
        <img src="/media/immersive-v2/hareketin-yankisi.webp" alt="" />
        <div className="home-discovery__herooverlay">
          <div>
            <h2>{c('Bir ürün değil, çalışan bir deneyim sistemi.', 'More than a product: an operating experience system.')}</h2>
            <p>{c('İçeriği keşfedin, teklif akışını başlatın; satın alımdan sonra cihaz ve oturum yönetimi burada açılsın.', 'Explore content and start a quote flow; device and session management opens here after purchase.')}</p>
            <Link className="btn btn--inverse btn--md" to="/kesfet">{c('Deneyimleri incele', 'Explore experiences')}</Link>
          </div>
        </div>
      </section>

      <section aria-labelledby="technology-title">
        <div className="home-discovery__sectionhead">
          <div>
            <h2 id="technology-title">{c('Teknoloji alanları', 'Technology areas')}</h2>
            <p>{c('Hub tek bir ekran türüne göre değil, Wondeon’dan aldığınız bütün deneyimlere göre büyür.', 'Hub grows around every experience you receive from Wondeon, not around a single screen type.')}</p>
          </div>
        </div>
        <div className="home-discovery__technologies">
          {technologies.map((technology) => (
            <article className="home-discovery__technology" key={technology.name}>
              <img src={technology.image} alt="" loading="lazy" />
              <div>
                <h3 lang={technology.language}>{c(technology.name, technology.nameEn)}</h3>
                <p>{c(technology.text, technology.textEn)}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {orderedItems.length > 0 && (
        <section aria-labelledby="catalog-title">
          <div className="home-discovery__sectionhead">
            <div><h2 id="catalog-title">{c('Katalogdan seçilenler', 'Selected from the catalog')}</h2><p>{c('Yayındaki ürünleri inceleyin; ticari süreç yalnızca “Teklif Al” üzerinden ilerler.', 'Browse published products; the commercial process proceeds only through “Request a Quote”.')}</p></div>
            <Link to="/kesfet">{c('Tümünü gör →', 'View all →')}</Link>
          </div>
          <div className="grid-covers">
            {orderedItems.slice(0, 4).map((item) => <ProductCard key={item.id} item={item} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function WeekAgenda({ now, sessions }: { now: Date; sessions: SessionItem[] }) {
  const { language, locale } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const days = Array.from({ length: 7 }, (_, index) => {
    const day = new Date(now);
    day.setHours(0, 0, 0, 0);
    day.setDate(day.getDate() + index);
    const localDay = day.toLocaleDateString('sv-SE');
    const count = sessions.filter((item) => item.startsAt.slice(0, 10) === localDay).length;
    return { day, count };
  });

  return (
    <Card title={c('Bu hafta', 'This week')} className="home-week">
      <ol className="home-week__days">
        {days.map(({ day, count }) => (
          <li key={day.toISOString()} className={day.toDateString() === now.toDateString() ? 'is-today' : ''}>
            <strong>{day.toLocaleDateString(locale, { day: '2-digit' })}</strong>
            <span>{day.toLocaleDateString(locale, { weekday: 'short' })}</span>
            <small>{count > 0 ? `${count} ${c('oturum', 'sessions')}` : c('Oturum yok', 'No sessions')}</small>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** Keşfet vitrini. Otomatik geçiş yok; kullanıcı seçimi sabit kalır. */
export function BannerCarousel({ slides, loading, rail = false }: { slides: BannerSlide[]; loading: boolean; rail?: boolean }) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const [index, setIndex] = useState(0);
  if (loading) return <div className="banner banner--skeleton" aria-hidden="true" />;

  if (slides.length === 0) {
    return (
      <section className="banner banner--brand" aria-label="Wondeon Hub">
        <img className="banner__img" src="/covers/hero-banner.jpg" alt="" />
        <div className="banner__overlay banner__overlay--brand"><div className="banner__content">
          <h2 className="banner__display">{c('Ekranı izlenen değil, katılınan bir sahneye dönüştürün.', 'Turn a screen from something watched into a stage people join.')}</h2>
          <p className="banner__sub">{c('Kamusal ekranlar için etkileşimli deneyimleri keşfedin.', 'Explore interactive experiences for public screens.')}</p>
          <Link className="btn btn--primary btn--md" to="/kesfet">{c('Seçkiyi incele', 'Explore the collection')}</Link>
        </div></div>
      </section>
    );
  }

  const safeIndex = Math.min(index, slides.length - 1);
  const slide = slides[safeIndex]!;
  const withRail = rail && slides.length > 1;
  return (
    <section className={`showcase ${withRail ? 'showcase--rail' : ''}`} aria-label={c('Öne çıkanlar', 'Featured')}>
      <div className="banner">
        <img className="banner__img" src={slide.imageUrl} alt="" />
        <div className="banner__overlay"><div className="banner__content">
          <h2 className="banner__title">{slide.title}</h2>
          {slide.subtitle && <p className="banner__sub">{slide.subtitle}</p>}
          {slide.ctaLabel && slide.ctaHref && <a className="btn btn--primary btn--md" href={slide.ctaHref}>{slide.ctaLabel}</a>}
        </div></div>
      </div>
      {withRail && (
        <div className="showcase__rail" aria-label={c('Vitrin seçimi', 'Showcase selection')}>
          {slides.map((item, itemIndex) => (
            <button key={item.id} type="button" className={`showcase__railitem ${itemIndex === safeIndex ? 'is-active' : ''}`} aria-pressed={itemIndex === safeIndex} onClick={() => setIndex(itemIndex)}>
              <span className="showcase__thumb">{item.imageUrl && <img src={item.imageUrl} alt="" loading="lazy" />}</span>
              <span className="showcase__railtitle">{item.title}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

const CATEGORY_HUES = ['violet', 'cyan', 'green', 'amber'] as const;
export function categoryHue(category: string): (typeof CATEGORY_HUES)[number] {
  let hash = 0;
  for (let index = 0; index < category.length; index += 1) hash = (hash * 31 + category.charCodeAt(index)) >>> 0;
  return CATEGORY_HUES[hash % CATEGORY_HUES.length]!;
}

export function categoryOrder(a: string, b: string): number {
  return compareCatalogDisplayOrder(a, b);
}

function LockIcon() {
  return <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true"><rect x="3" y="7" width="10" height="7" rx="1.6" fill="currentColor" /><path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>;
}

export function ProductCard({ item }: { item: CatalogItem }) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const hue = categoryHue(item.category);
  const cover = catalogCover(item);
  const category = canonicalCatalogName(item.category);
  return (
    <Link to={`/kesfet/${item.slug}`} className="pcard">
      <div className={`pcard__cover pcard__cover--${hue}`}>
        {cover ? <img src={cover} alt="" loading="lazy" /> : <span className="pcard__monogram" aria-hidden="true">{item.name.trim().charAt(0).toLocaleUpperCase('tr')}</span>}
        {!item.entitled && <span className="pcard__lock" title={c('Hesabınıza tanımlı değil', 'Not assigned to your account')}><LockIcon /></span>}
      </div>
      <div className="pcard__meta">
        <span className="pcard__kicker" lang={catalogNameLanguage(category)}><span className={`catdot catdot--${hue}`} aria-hidden="true" />{localizeCatalogName(category, language)}</span>
        <h3 className="pcard__name">{item.name}</h3>
        {item.entitled ? <StatusDot tone="success" label={item.latestVersion ? `v${item.latestVersion}` : c('Hazır', 'Ready')} /> : <span className="small muted">{c('Teklif Al', 'Request a Quote')}</span>}
      </div>
    </Link>
  );
}

export function ProductRow({ item }: { item: CatalogItem }) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const hue = categoryHue(item.category);
  const thumb = catalogThumb(item);
  return (
    <Link to={`/kesfet/${item.slug}`} className="prow">
      <span className={`prow__thumb pcard__cover--${hue}`}>{thumb ? <img src={thumb} alt="" loading="lazy" /> : <span className="prow__mono" aria-hidden="true">{item.name.trim().charAt(0).toLocaleUpperCase('tr')}</span>}</span>
      <span className="prow__meta"><span className="prow__name">{item.name}</span>{item.entitled ? <StatusDot tone="success" label={item.latestVersion ? `v${item.latestVersion}` : c('Hazır', 'Ready')} /> : <span className="small muted">{c('Teklif Al', 'Request a Quote')}</span>}</span>
    </Link>
  );
}

function CalendarIcon() {
  return <svg viewBox="0 0 32 32" width="48" height="48" fill="none" aria-hidden="true"><rect x="5" y="8" width="22" height="19" rx="4" stroke="currentColor" strokeWidth="1.6" /><path d="M5 13h22M11 5v6M21 5v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>;
}
function ScreenIcon() {
  return <svg viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true"><rect x="2.5" y="3" width="15" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5" /><path d="M7 17h6M10 14v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>;
}
function DocumentIcon() {
  return <svg viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true"><path d="M5 2.5h6l4 4v11H5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><path d="M11 2.5v4h4" stroke="currentColor" strokeWidth="1.5" /></svg>;
}
function CreditIcon() {
  return <svg viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true"><rect x="2.5" y="4" width="15" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" /><path d="M2.5 8h15M6 12h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>;
}

function sessionStatusEnglish(status: string): string {
  return ({ scheduled: 'Scheduled', running: 'Running', completed: 'Completed', stopped: 'Stopped', failed: 'Failed' } as Record<string, string>)[status] ?? status;
}
